// Process-wide org money formatting for module-level formatters that can't use
// React hooks (report/dashboard helpers, chart configs, etc.).
//
// The org is fixed per authenticated session, so we set the locale + default
// currency once after /users/me loads (see Protected in App.tsx) and read it
// from any plain function. Components that CAN use hooks should prefer
// `useOrgFormatting`; both resolve the locale via the same `locale.ts` logic.

import { resolveFormattingLocale, type UiLanguage } from './locale';

type OrgFormatState = { locale: string; currency: string };

// Sensible defaults until /users/me sets the real org context.
let current: OrgFormatState = { locale: 'en-US', currency: 'USD' };

export function setOrgFormat(input: { language?: unknown; country?: unknown; currency?: unknown }): void {
  const lang: UiLanguage = input.language === 'es' || input.language === 'it' ? input.language : 'en';
  current = {
    locale: resolveFormattingLocale(lang, input.country),
    currency: (typeof input.currency === 'string' && input.currency.trim()) || 'USD',
  };
}

export function getOrgCurrency(): string {
  return current.currency;
}

export function getOrgLocale(): string {
  return current.locale;
}

export type MoneyFormatOptions = {
  /** Override the org default currency (e.g. an invoice's own currency). */
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact';
};

/** Format money in the org locale + currency. Never throws on a bad currency. */
export function formatMoney(value: number | null | undefined, options: MoneyFormatOptions = {}): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : Number(value || 0);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const currency = (options.currency && options.currency.trim()) || current.currency;
  try {
    return new Intl.NumberFormat(current.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
      notation: options.notation,
    }).format(safeAmount);
  } catch {
    return new Intl.NumberFormat(current.locale, {
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
      notation: options.notation,
    }).format(safeAmount);
  }
}
